import { getErrorMessage } from 'helpers/candidate_application/form-page-helpers';
import { filterUploadsForField } from 'helpers/document-upload-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { arrayPath, readValueByName } from 'helpers/field-paths';
import addValidators from 'helpers/validators/apply-validators';
import { useKeyboardHints } from 'hooks/keyboard-hints';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';

import { InputGroup } from '../InputGroup/InputGroup';

const SubFieldWithKeyboardHints = ({
  arrayIndex,
  subField,
  fieldName,
  parentCode,
  childCode,
  control,
  register,
  setValue,
  trigger,
  getValues,
  error,
  componentMap,
  brand,
  candidateId,
  handlers,
  getComponentProps,
  uploads = [],
  fieldPathIndex,
  allValues,
  sectionHeaderSpacing,
  isVisible = true,
}) => {
  const keyboardHint = useKeyboardHints(subField?.type);

  const filteredUploads = filterUploadsForField(uploads, fieldName);
  const enrichedSubField = {
    ...subField,
    metadata: {
      ...(subField?.metadata || {}),
      field_code: fieldName,
      ...(parentCode != null && { parent_code: parentCode }),
      ...(childCode != null && { child_code: childCode }),
      ...(arrayIndex != null && { array_index: arrayIndex }),
    },
  };

  const getComponentPropsForSubField = value =>
    getComponentProps({
      question: enrichedSubField,
      value,
      candidateId,
      handlers,
      brand,
      fieldPathIndex: fieldPathIndex || {},
      allValues: allValues || {},
      uploads: filteredUploads,
    });

  const validationRules = isVisible
    ? addValidators(
        subField.type,
        subField.min,
        subField.max,
        subField.required,
        subField.validatorName,
        subField.validationParams,
        getValues,
        fieldName,
      )
    : {};

  return (
    <QuestionWrapper
      key={fieldName}
      fieldName={fieldName}
      question={subField}
      control={control}
      register={register}
      setValue={setValue}
      trigger={trigger}
      getValues={getValues}
      error={error}
      componentMap={componentMap}
      requirementVariantStyle="text"
      gap="small"
      titleSize="small"
      hideHeader={false}
      noMargin={true}
      sectionHeaderSpacing={sectionHeaderSpacing}
      sectionHeader={subField.sectionHeader}
      keyboardHint={keyboardHint}
      getComponentProps={getComponentPropsForSubField}
      validators={validationRules}
      isVisible={isVisible}
    />
  );
};

export const FixedArray = ({
  question,
  control,
  register,
  setValue,
  trigger,
  getValues,
  error,
  componentMap,
  brand,
  candidateId,
  handlers,
  subFields,
  arrayItemTitles,
  size,
  name,
  validators,
  fieldPathIndex,
  allValues,
  errors,
  uploads = [],
  isVisible = true,
}) => {
  const { getComponentProps } = require('helpers/question-config');

  const arrayName = name || question?.code;
  const arrayFieldCode =
    question?.code || (arrayName?.includes('.') ? arrayName.split('.').pop() : arrayName);
  const subFieldsToUse = subFields || question?.subFields || [];
  const arraySubFieldCodes = subFieldsToUse.map(subField => subField.code).filter(Boolean);
  const arrayItemTitlesToUse = arrayItemTitles || question?.arrayItemTitles;
  const sizeToUse = size || question?.size || 0;
  const title = question?.title || question?.text;

  const staticArrayPositions = [];
  for (let i = 0; i < sizeToUse; i++) {
    staticArrayPositions[i] = i;
  }

  const valueFromArrayItemTitle = itemTitle => {
    if (itemTitle == null) return '';
    if (typeof itemTitle === 'string' || typeof itemTitle === 'number') return String(itemTitle);
    if (typeof itemTitle === 'object' && !Array.isArray(itemTitle)) {
      const values = Object.values(itemTitle).filter(v => v != null && String(v).trim() !== '');
      return values.length > 0 ? values.join(' - ') : '';
    }
    return '';
  };

  const isSubFieldVisible = (subField, arrayIndex) => {
    if (!subField?.conditions) return true;
    return evaluateFieldConditions(subField.conditions, allValues || {}, {
      parentCode: arrayFieldCode,
      arrayIndex,
      fieldPathIndex,
      arraySubFieldCodes,
    });
  };

  const renderSubField = (subField, arrayIndex, isFirstVisible) => {
    if (!isSubFieldVisible(subField, arrayIndex)) {
      return null;
    }

    let subFieldData = subField;
    if (subField?.useArrayItemAsDefault) {
      if (Array.isArray(arrayItemTitlesToUse)) {
        const arrayItemTitle = arrayItemTitlesToUse[arrayIndex] ?? arrayItemTitlesToUse[0];
        const defaultValue = valueFromArrayItemTitle(arrayItemTitle) || '';
        subFieldData = { ...subField, defaultValue };
      } else {
        const defaultValue = valueFromArrayItemTitle(arrayItemTitlesToUse) || '';
        subFieldData = { ...subField, defaultValue };
      }
    }

    const fullFieldName = arrayPath(arrayName, arrayIndex, subField.code);

    return (
      <SubFieldWithKeyboardHints
        key={fullFieldName}
        arrayIndex={arrayIndex}
        fieldName={fullFieldName}
        parentCode={arrayFieldCode}
        childCode={subFieldData.code}
        subField={subFieldData}
        control={control}
        register={register}
        setValue={setValue}
        trigger={trigger}
        getValues={getValues}
        error={getErrorMessage(readValueByName(errors, fullFieldName))}
        brand={brand}
        candidateId={candidateId}
        componentMap={componentMap}
        handlers={handlers}
        getComponentProps={getComponentProps}
        uploads={uploads}
        fieldPathIndex={fieldPathIndex}
        allValues={allValues}
        sectionHeaderSpacing={isFirstVisible ? 'flush' : 'compact'}
        isVisible={isVisible}
      />
    );
  };

  const renderArrayItemSubFields = arrayIndex => {
    let isFirstVisible = true;
    return subFieldsToUse?.map(subField => {
      if (!isSubFieldVisible(subField, arrayIndex)) {
        return null;
      }
      const node = renderSubField(subField, arrayIndex, isFirstVisible);
      isFirstVisible = false;
      return node;
    });
  };

  const errorMessage = getErrorMessage(error);

  return (
    <InputGroup noMargin>
      <div className="d-flex flex-column u-gap-medium">
        {staticArrayPositions.map(arrayIndex => {
          let itemTitle;
          if (Array.isArray(arrayItemTitlesToUse)) {
            const itemData = arrayItemTitlesToUse[arrayIndex] ?? arrayItemTitlesToUse[0];
            itemTitle = valueFromArrayItemTitle(itemData) || title;
          } else {
            itemTitle = valueFromArrayItemTitle(arrayItemTitlesToUse) || title;
          }

          return (
            <InputGroup
              key={`${arrayName}-${arrayIndex}`}
              title={`${arrayIndex + 1}. ${itemTitle}`}
              noMargin>
              <div className="card card-with-border u-padding d-flex flex-column u-gap-small">
                {renderArrayItemSubFields(arrayIndex)}
              </div>
            </InputGroup>
          );
        })}
      </div>
      {errorMessage && <span className="error-message">{errorMessage}</span>}
    </InputGroup>
  );
};
